"use client";
import { Card, CardContent } from '@/components/ui/card';
import { Quote, Star } from 'lucide-react';
import type { VideoTestimonial } from '@/types/videoTestimonials';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { useIsMobile } from '@/hooks/use-mobile';
import { decodeHtmlEntitiesSSR } from '@/lib/utils';

interface TextTestimonialsDisplayProps {
  testimonials: VideoTestimonial[];
  layout?: 'grid' | 'carousel' | 'list';
  showRatings?: boolean;
}

/** One testimonial card, as the homepage shows it. Shared with /testimonials, which
 *  passes showPhoto={false}: its real customers have photos, but the page uses the same
 *  photo-less card as the homepage. */
export function TestimonialCard({
  testimonial,
  showRatings = false,
  showPhoto = true,
}: {
  testimonial: VideoTestimonial;
  showRatings?: boolean;
  showPhoto?: boolean;
}) {
  return (
    <Card className="border-0 shadow-soft hover:shadow-elegant transition-all duration-300 h-full">
      <CardContent className="p-6 sm:p-8 flex flex-col h-full">
        <Quote className="h-8 w-8 text-primary/20 mb-4" />

        {showRatings && (
          <div className="flex mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
        )}

        <blockquote className="text-lg font-medium leading-relaxed mb-6 flex-1">
          "{decodeHtmlEntitiesSSR(testimonial.quote)}"
        </blockquote>

        <div className="flex items-center gap-4">
          {/* Real photos only. No initials circle or company-letters badge as a stand-in:
              without a photo the card shows name, title and company alone. */}
          {showPhoto && testimonial.thumbnail_url && (
            <img
              src={testimonial.thumbnail_url}
              alt={testimonial.customer_name}
              className="w-14 h-14 flex-shrink-0 rounded-full object-cover shadow-soft"
              loading="lazy"
            />
          )}

          <div className="flex-1 min-w-0">
            <div className="font-semibold text-foreground flex items-center gap-2">
              {testimonial.customer_name}
              {testimonial.industry && (
                <span className="text-xs px-2.5 py-0.5 rounded-lg bg-accent text-primary/80 border border-primary/15 font-medium">
                  {testimonial.industry}
                </span>
              )}
            </div>
            <div className="text-sm text-muted-foreground truncate">
              {testimonial.customer_title}
            </div>
            <div className="text-xs text-muted-foreground font-medium">
              {testimonial.company_name}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Pure presentational testimonials grid/carousel — no data fetching, no
// Supabase import. Callers that already have their data (e.g. the homepage,
// which only needs the static fallback set) should use this directly instead
// of `TextTestimonials`, so importing it doesn't pull @supabase/supabase-js
// into their bundle just to render a handful of quotes.
export function TextTestimonialsDisplay({
  testimonials,
  layout = 'grid',
  showRatings = false,
}: TextTestimonialsDisplayProps) {
  const isMobile = useIsMobile();

  if (testimonials.length === 0) return null;

  const renderCard = (testimonial: VideoTestimonial) => (
    <TestimonialCard key={testimonial.id} testimonial={testimonial} showRatings={showRatings} />
  );

  if (isMobile) {
    return (
      <Carousel opts={{ align: 'start', loop: true }} className="w-full">
        <CarouselContent className="-ml-4">
          {testimonials.map((testimonial) => (
            <CarouselItem key={testimonial.id} className="pl-4 basis-[85%]">
              {renderCard(testimonial)}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    );
  }

  const gridClass = layout === 'grid' ? 'grid md:grid-cols-3 gap-8' :
                    layout === 'list' ? 'space-y-4' :
                    'flex gap-8 overflow-x-auto';

  return (
    <div className={gridClass}>
      {testimonials.map((testimonial) => renderCard(testimonial))}
    </div>
  );
}
